// What moves behind the landing page's first screen: a dot-matrix field over
// its top half, whose pixels come and go, and the veil that deepens as the page
// goes down. Where the screen is wide enough it also asks for the devices
// (assets/backdrop.js, ./backdrop.ts), which carry three.js: a phone, or a
// visitor saving data, keeps their picture.

const COLOURS = ["#ffd23f", "#ff5f9e", "#3fd0e8", "#a98bff", "#ffb45c"];
const WHITE = "#fcf6ff";
const CELL = 12;
const DOT = 4;
const DEVICES_FROM = 760;

/** How strongly a height of the window holds pixels: full at the top, gone by a little over half way. */
const upper = (height: number) => Math.max(0, Math.min(1, (0.62 - height) / 0.42));

export function startPixels(): void {
  const field = document.querySelector<HTMLCanvasElement>("canvas.pixels");
  if (!field) return;
  const g = field.getContext("2d");
  if (!g) return;
  const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const root = document.documentElement;

  let cols = 0, rows = 0, phase = new Float32Array(0), tint: string[] = [];
  const size = () => {
    field.width = innerWidth;
    field.height = innerHeight;
    cols = Math.ceil(innerWidth / CELL);
    rows = Math.ceil(innerHeight / CELL);
    phase = new Float32Array(cols * rows).map(() => Math.random() * 100);
    tint = Array.from({ length: cols * rows }, () => (Math.random() < 0.6 ? WHITE : COLOURS[Math.floor(Math.random() * COLOURS.length)]!));
  };

  const draw = (clock: number) => {
    const scrolled = scrollY / innerHeight;
    root.style.setProperty("--deep", String(Math.min(1, scrolled * 0.9)));
    g.clearRect(0, 0, field.width, field.height);
    // The pixels belong to the first screen: they thin out as the page goes down.
    const fade = Math.max(0, 1 - scrolled * 1.3);
    if (fade <= 0.01) return;
    for (let y = 0; y < rows; y++) {
      const mask = upper(y / rows);
      if (mask <= 0) continue;
      for (let x = 0; x < cols; x++) {
        const i = y * cols + x;
        const wave = Math.sin(x * 0.19 + clock * 0.6) * Math.sin(y * 0.23 - clock * 0.45) + Math.sin((x - y) * 0.07 + clock * 0.3);
        const twinkle = Math.sin(clock * 1.7 + phase[i]!) * 0.5 + 0.5;
        const on = (wave * 0.35 + 0.5) * mask * twinkle;
        if (on < 0.32) continue;
        g.globalAlpha = Math.min(0.55, (on - 0.32) * 1.6) * fade;
        g.fillStyle = tint[i]!;
        g.fillRect(x * CELL + (CELL - DOT) / 2, y * CELL + (CELL - DOT) / 2, DOT, DOT);
      }
    }
    g.globalAlpha = 1;
  };

  size();
  if (still) {
    // A field that holds still, drawn again only when the window or the scroll changes it.
    const once = () => draw(3);
    addEventListener("resize", () => { size(); once(); });
    addEventListener("scroll", once, { passive: true });
    once();
  } else {
    addEventListener("resize", size);
    let next = 0;
    const loop = (now: number) => {
      requestAnimationFrame(loop);
      if (document.hidden || now < next) return;
      next = now + 1000 / 15;
      draw(now / 1000);
    };
    requestAnimationFrame(loop);
  }

  const saving = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;
  if (innerWidth >= DEVICES_FROM && !saving) {
    const script = document.createElement("script");
    script.type = "module";
    script.src = "/assets/backdrop.js";
    document.head.append(script);
  }
}
